import Component from "@glimmer/component";
import { service } from "@ember/service";
import { on } from "@ember/modifier";
import { array, fn } from "@ember/helper";
import { t } from "ember-intl";
import { not } from "ember-truth-helpers";
import canManage from "glesys-controlpanel/helpers/can-manage";
import Entity from "glesys-controlpanel/components/entity";
import EntityField from "glesys-controlpanel/components/entity/fields/field";
import EntityDataCenterField from "glesys-controlpanel/components/entity/fields/data-center";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import EntityList from "glesys-controlpanel/components/entity-list";
import EmptyState from "glesys-controlpanel/components/empty-state";
import IconsSidebarStorage from "glesys-controlpanel/components/icons/sidebar/storage";
import StatusSpinner from "glesys-controlpanel/components/icons/status-spinner";
import PageContent from "glesys-controlpanel/components/page-content";
import InfoPopup from "glesys-controlpanel/components/info-popup";

export default class BlockStorageVolumesOverview extends Component {
  @service modal;
  @service intl;

  get project() {
    return this.args.model.project;
  }

  get volumes() {
    return this.args.model.volumes;
  }

  openDelete = (volume) => {
    if (volume.attachment) {
      this.modal.open("modals/info", {
        titleText: this.intl.t("block-storage-volumes.details.modal.delete-attached-title"),
        text: this.intl.t("block-storage-volumes.details.modal.delete-attached-info"),
      });

      return;
    }

    this.modal.open("modals/organism-delete", {
      entity: volume.name,
      task: volume.deleteTask,
      entityKey: volume.id,
    });
  };

  <template>
    <PageContent>
      <EntityList @items={{this.volumes}}>
        <:default as |volume|>
          <Entity
            @route="block-storage.volumes.details"
            @model={{volume.id}}
            @status={{volume.statusIndicator}}
            @title={{volume.name}}
            @key={{volume.id}}
            @linkLabel={{t "block-storage-volumes.manage-volume-aria-label" name=volume.name}}
            @hideActions={{not (canManage)}}
          >
            <:fields>
              <EntityDataCenterField @dataCenter={{volume.dataCenter}} />
              <EntityField data-test-block-storage-volume-size @label={{t "label.size"}}>{{volume.sizeInGib}}
                GiB</EntityField>
              <EntityField data-test-block-storage-volume-attachment @label={{t "label.attached"}}>
                {{if volume.attachment volume.attachment.serverid (t "block-storage-volumes.not-attached")}}
              </EntityField>
              <EntityField data-test-block-storage-volume-wwn @label={{t "block-storage-volumes.wwn"}}>
                <span class="inline-flex items-center gap-1.5">
                  {{#if volume.wwn}}
                    <span data-test-block-storage-volume-wwn-value>{{volume.wwn}}</span>
                  {{else}}
                    <StatusSpinner data-test-block-storage-volume-wwn-spinner />
                  {{/if}}
                  <InfoPopup>{{t "block-storage-volumes.wwn-description"}}</InfoPopup>
                </span>
              </EntityField>
            </:fields>
            <:actions>
              <DropdownDestructiveItem
                data-test-block-storage-volume-delete
                disabled={{not volume.isReady}}
                {{on "click" (fn this.openDelete volume)}}
              >
                {{t "button.delete"}}
              </DropdownDestructiveItem>
            </:actions>
          </Entity>
        </:default>
        <:empty>
          <EmptyState
            @icon={{IconsSidebarStorage}}
            @title={{t "block-storage-volumes.empty-state.title"}}
            @description={{t "block-storage-volumes.empty-state.description"}}
            @points={{array
              (t "block-storage-volumes.empty-state.usp-list.tiers")
              (t "block-storage-volumes.empty-state.usp-list.grow")
              (t "block-storage-volumes.empty-state.usp-list.move-between-servers")
            }}
            @learnMoreUrl="https://docs.glesys.com/products/storage/block-storage"
            @learnMoreAriaLabel={{t "block-storage-volumes.empty-state.learn-more-aria-label"}}
            @createRoute={{if (canManage) "block-storage.volumes.create"}}
            @createLabel={{t "button.create-block-storage-volume"}}
          />
        </:empty>
      </EntityList>
    </PageContent>
  </template>
}
